/* global window */

import get from 'lodash/get';
import some from 'lodash/some';
import { calculateViewport, isBrowserEdge } from './utils';

/**
 * Is sticky player enabled and is viewport wide enough.
 * @return {boolean}.
 */
export function isStickyActive() {
  const viewportWidth = calculateViewport().width;
  return window.HTV_STICKY_PLAYER_ENABLED && viewportWidth > window.HTV_STICKY_PLAYER_MIN_WIDTH
    && !isBrowserEdge(window.navigator);
}

/**
 * Is player up next overlay is active.
 * @return {boolean}.
 */
export function isUpNextOverlayActive(player) {
  const overlay = player.el().getElementsByClassName('vjs-overlay')[0];

  if (!overlay) {
    return false;
  }

  return !overlay.classList.contains('vjs-hidden');
}

/**
 * Triggers global event 'hearstPlayer:events:add_sticky'.
 */
export function triggerAddStickyEvent() {
  const evt = document.createEvent('CustomEvent');
  evt.initCustomEvent('hearstPlayer:events:add_sticky', false, false, null);
  window.dispatchEvent(evt);
}

/**
 * Triggers global event 'hearstPlayer:events:remove_sticky'.
 */
export function triggerRemoveStickyEvent() {
  const evt = document.createEvent('CustomEvent');
  evt.initCustomEvent('hearstPlayer:events:remove_sticky', false, false, null);
  window.dispatchEvent(evt);
}

/**
 * Triggers global event 'hearstPlayer:events:play_sticky'.
 */
export function triggerPlayStickyEvent() {
  const evt = document.createEvent('CustomEvent');
  evt.initCustomEvent('hearstPlayer:events:play_sticky', false, false, null);
  window.dispatchEvent(evt);
}

/**
 * Called when a potentially sticky player enters viewport.
 * Check if there is a current sticky player and if it is playing ad or content.
 * If so, then do not play,
 * @param {Object} encore - Encore video player instance.
 */
export function stickyEnterViewHandler(encore) {
  if (!isStickyActive() || !encore.source.canBeSticky) {
    return;
  }

  if (some(window.hearstPlayer.instances, instance => instance.player.isFullscreen())) {
    return;
  }

  const currentStickyInstance = window.hearstPlayer.getStickyInstance();

  if (currentStickyInstance) {
    if (currentStickyInstance.player === encore.player) {
      triggerRemoveStickyEvent();
    }

    const currentStickyPlayer = currentStickyInstance.player;

    // if preroll is running, do not autoplay
    if (get(currentStickyPlayer, 'ima.controller.sdkImpl.adsActive', false)
      && currentStickyPlayer.ima.controller.sdkImpl.isAdPlaying()) {
      return;
    }

    // if content is running, do not autoplay
    if (!get(currentStickyPlayer, 'ima.controller.sdkImpl.adsActive', false)
      && !currentStickyPlayer.paused()) {
      return;
    }

    // if up next overlay is active, do not autoplay
    if (isUpNextOverlayActive(currentStickyPlayer)) {
      return;
    }
  }

  if (encore.source.skipEnterEvent) {
    encore.source.setSkipEnterEvent(false);
    return;
  }

  if (!encore.player.hasStarted()) {
    encore.player.trigger('inViewPlay');
    return;
  }

  if (get(encore.player, 'ima.controller.sdkImpl.adsActive', false)) {
    encore.player.ima.resumeAd();
  } else {
    encore.player.play();
  }
}

/**
 * Called when a potentially sticky player exits viewport.
 * Check if there is a current sticky player and if it has played content at least 7 seconds.
 * If so, the do not play,
 * @param {Object} encore - Encore video player instance.
 */
export function stickyExitViewHandler(encore) {
  if (!isStickyActive() || !encore.source.canBeSticky) {
    return;
  }

  if (encore.source.skipExitEvent) {
    encore.source.setSkipExitEvent(false);
    return;
  }

  const currentStickyInstance = window.hearstPlayer.getStickyInstance();

  // if no current sticky player, make this sticky
  if (!currentStickyInstance) {
    encore.source.setIsSticky(true);
    encore.source.setSkipEnterEvent(true);
    triggerAddStickyEvent(encore.player.playerElementId);
  } else {
    const currentStickyPlayer = currentStickyInstance.player;

    // if current sticky player preroll is running, do not replace
    if (get(currentStickyPlayer, 'ima.controller.sdkImpl.adsActive', false)
      && currentStickyPlayer.ima.controller.sdkImpl.isAdPlaying()) {
      return;
    }

    // if current sticky player content is running, do not replace
    if (!get(currentStickyPlayer, 'ima.controller.sdkImpl.adsActive', false)
      && !currentStickyPlayer.paused()) {
      return;
    }

    // if current sticky player up next overlay is active, do not replace
    // if up next overlay is active, do not autoplay
    if (isUpNextOverlayActive(currentStickyPlayer)) {
      return;
    }

    // if we are here replace the current sticky player with this one
    currentStickyInstance.source.setSkipExitEvent(true);
    triggerRemoveStickyEvent();
    encore.source.setIsSticky(true);
    encore.source.setSkipEnterEvent(true);
    triggerAddStickyEvent();
  }
}

/**
 * Attach sticky play events to video player.
 * @param {Object} Encore - Encore video player instance.
 */
export function attachStickyPlayerEvents(encore) {
  encore.player.on('play', () => {
    const currentStickyInstance = window.hearstPlayer.getStickyInstance();
    if (currentStickyInstance) {
      if (encore.playerElementId !== currentStickyInstance.playerElementId) {
        triggerRemoveStickyEvent();
        currentStickyInstance.source.setSkipExitEvent(true);
      } else {
        triggerPlayStickyEvent();
      }
    }
  });

  encore.player.on('ads-ima-resume', () => {
    const currentStickyInstance = window.hearstPlayer.getStickyInstance();
    if (currentStickyInstance) {
      if (encore.playerElementId !== currentStickyInstance.playerElementId) {
        triggerRemoveStickyEvent();
        currentStickyInstance.source.setSkipExitEvent(true);
      }
    }
  });
}




// WEBPACK FOOTER //
// ./src/stickyplayer.js